import { cache } from '@lib/graphql/cache'
import { Language } from '@lib/graphql/havelock/__generated__/graphql'
import { GET_USER_ACCOUNT } from '@lib/graphql/havelock/queries/organization'
import { translate } from '@translations/TranslationsProvider/TranslationsProvider'
import { TFunction } from '@translations/useTranslations'

export interface LocaleBundle {
  translations: Record<string, any>
  formLocale: Record<string, any>
  gridLocale: Record<string, any>
}

const localeLoaders: Partial<Record<Language, () => Promise<LocaleBundle>>> = {
  [Language.En]: () =>
    import('@locales/bundles/en').then((module) => module.default),
  [Language.Bg]: () =>
    import('@locales/bundles/bg').then((module) => module.default),
  [Language.Fr]: () =>
    import('@locales/bundles/fr').then((module) => module.default)
}

const loadedLocales: Record<string, LocaleBundle | undefined> = {}
const localePromises: Record<string, Promise<LocaleBundle> | undefined> = {}

export const loadLocale = (lang: Language): Promise<LocaleBundle> => {
  if (loadedLocales[lang]) return Promise.resolve(loadedLocales[lang])

  if (!localePromises[lang]) {
    const loader = localeLoaders[lang]
    if (!loader) throw new Error(`Translations missing for language '${lang}'`)

    localePromises[lang] = loader().then(
      (locale) => {
        loadedLocales[lang] = locale
        return locale
      },
      (err) => {
        localePromises[lang] = undefined
        throw err
      }
    )
  }

  return localePromises[lang]
}

export const loadAllLocales = () =>
  Promise.all(
    Object.keys(localeLoaders).map((lang) => loadLocale(lang as Language))
  )

export const getLoadedTranslations = (lang: string) =>
  loadedLocales[lang]?.translations

export const getLoadedLocale = (lang: Language) => loadedLocales[lang]

export const DEFAULT_LOCALE = Language.En
export const EN_LOCALE = Language.En

export const translateActiveLocale: TFunction = (key, keywords) => {
  const data = cache.readQuery({ query: GET_USER_ACCOUNT })
  const lang = data?.getCurrentUserAccount?.lang ?? DEFAULT_LOCALE

  return translate(key, keywords, {
    translations: loadedLocales[lang]?.translations
  })
}
